import { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { useCreateLabVisit, useCreateInternalNote } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export function CreateLabVisitButton({
  userId,
  userName,
}: {
  userId: string;
  userName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createLabVisit = useCreateLabVisit();
  const createInternalNote = useCreateInternalNote();
  const isSubmitting = createLabVisit.isPending || createInternalNote.isPending;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError("Begrunnelse er påkrevd");
      return;
    }
    setError(null);
    try {
      const labVisit = await createLabVisit.mutateAsync({
        authUserId: userId,
        status: "paid",
        selfReported: false,
      });
      await createInternalNote.mutateAsync({
        labVisitId: labVisit.id,
        author: "admin",
        note: reason.trim(),
      });
      setOpen(false);
      setReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Noe gikk galt");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={isSubmitting}
      >
        <Gift className="h-4 w-4 mr-1" />
        Nytt labbesøk
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Opprett labbesøk{userName ? ` for ${userName}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-admin-text-primary">
                Begrunnelse
              </label>
              <Textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError(null);
                }}
                placeholder="Skriv en begrunnelse for labbesøket..."
                className="mt-1.5"
                rows={3}
              />
              {error && <p className="text-sm text-admin-danger-500 mt-1">{error}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Avbryt
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : null}
                Opprett
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
